/**
 * Circulation — decide which shared walls get doors so the floor is fully
 * connected (every cell reachable). Builds a spanning tree over the cell
 * adjacency graph (guarantees connectivity with no redundant doors), then adds a
 * few extra "loop" doors so there's more than one route.
 *
 * The tree is PRIORITISED, which is what makes the layout read as a building
 * rather than a warren: a door onto a corridor outranks a door into the next
 * room, so rooms open onto circulation wherever circulation is available and
 * room-to-room doors survive only where a cell has no corridor wall at all.
 * Cells already joined by an opened archway (the corridor ring) are passed in
 * as `merged` so the tree doesn't waste doors re-linking them.
 *
 * Pure + deterministic: the caller passes a seeded rng.
 */

import type { Adjacency } from './bsp';

class UnionFind {
  private readonly parent: number[];
  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i);
  }
  find(x: number): number {
    let r = x;
    while (this.parent[r] !== r) r = this.parent[r]!;
    while (this.parent[x] !== r) {
      const next = this.parent[x]!;
      this.parent[x] = r;
      x = next;
    }
    return r;
  }
  union(a: number, b: number): boolean {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra === rb) return false;
    this.parent[ra] = rb;
    return true;
  }
}

export interface DoorChoiceOpts {
  /** Fraction of leftover adjacencies opened as alternate routes. */
  extraLoopFrac?: number;
  /** Lower = opened first by the spanning tree (e.g. 0 for room→corridor). */
  priority?: (e: Adjacency) => number;
  /** Groups of cells already connected without a door (opened archways). */
  merged?: readonly (readonly number[])[];
}

/**
 * Choose the doorways to open. `leafCount` cells, `adj` their wall-adjacencies.
 * Returns a subset of `adj` — a spanning tree (all cells connected) plus a
 * fraction `extraLoopFrac` of the leftover adjacencies for alternate routes.
 */
export function chooseDoors(
  leafCount: number,
  adj: Adjacency[],
  rng: () => number,
  opts: DoorChoiceOpts = {},
): Adjacency[] {
  const { extraLoopFrac = 0.25, priority, merged = [] } = opts;
  // Shuffle a copy (Fisher–Yates with the seeded rng) so the tree isn't biased.
  const shuffled = adj.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  // Stable sort by priority, so the shuffle still varies order within a tier.
  if (priority) shuffled.sort((a, b) => priority(a) - priority(b));
  const uf = new UnionFind(leafCount);
  // Cells joined by an archway are one space before any door is placed.
  for (const group of merged) {
    for (let i = 1; i < group.length; i++) uf.union(group[0]!, group[i]!);
  }
  const doors: Adjacency[] = [];
  const leftover: Adjacency[] = [];
  for (const e of shuffled) {
    if (uf.union(e.a, e.b)) doors.push(e);
    else leftover.push(e);
  }
  for (const e of leftover) {
    if (rng() < extraLoopFrac) doors.push(e);
  }
  return doors;
}
